import * as Sentry from '@sentry/browser'
import { Device, Call } from '@twilio/voice-sdk'
import { Service } from '@/services/helpers/ServiceHelper'

const FIRST_RETRY_DELAY = 2000
const MAX_RETRY_DELAY = 30 * 1000
const MAX_RETRIES = 5

export function useCallDevice () {
  const snackbar = useSnackbar()
  const callStore = useCallStore()
  const getToken = () => Service.$axiosInstance.$get('/api/sms-threads/token')

  const permissions = usePermissions({
    showExplanationDialog: true,
    onDenied: () => {
      Sentry.captureMessage('Microphone permission denied by user')
    }
  })

  const callDevice = shallowRef<Device | null>(null)

  let deviceWanted = false
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let retryDelay = FIRST_RETRY_DELAY
  let retriesLeft = MAX_RETRIES

  // shallowRef: updates require reference replacement (`activeCalls.value = new Map(...)`).
  // Mutations via .set/.delete/.clear will NOT trigger reactivity.
  const activeCalls = shallowRef(new Map<string, Call>())

  const ringtoneAudio = ref<HTMLAudioElement | null>(null)

  const startRingtone = () => {
    if (ringtoneAudio.value) { return }
    const audio = new Audio('/sounds/incoming-call-notification.mp3')
    audio.loop = true
    audio.play().catch(() => {})
    ringtoneAudio.value = audio
  }

  const stopRingtone = () => {
    if (!ringtoneAudio.value) { return }
    ringtoneAudio.value.pause()
    ringtoneAudio.value.currentTime = 0
    ringtoneAudio.value = null
  }

  watch(
    () => callStore.incomingRingingCallId,
    (id) => {
      const INITIALIZED_CONFERENCE = id?.startsWith('CF')
      if (INITIALIZED_CONFERENCE) { startRingtone() }
    }
  )

  const micPermission = computed(() => {
    const state = permissions.state('microphone')
    return { state } as PermissionStatus
  })

  const audio = useAudioSources(callDevice)

  const requestMicPermission = async () => {
    try {
      const result = await permissions.request('microphone')

      if (!result.isGranted) {
        return false
      }

      if (!audio.inputSource.value || !audio.outputSource.value) {
        await audio.setAudioSources()
      }
      return true
    } catch (error) {
      Sentry.captureException(error)
      return false
    }
  }

  const cancelRetry = () => {
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
  }

  // Drops the device but still wants one, so a failed attempt can be retried.
  const discardDevice = async () => {
    const device = callDevice.value
    activeCalls.value = new Map<string, Call>()
    if (!device) { return }
    // Detached first: an orphan that kept its listeners would tear down whatever replaces it.
    device.removeAllListeners()
    audio.removeAudioSources()
    callDevice.value = null
    try {
      // Throws unless the device is Registered, and destroy() is what frees the resources.
      await device.unregister()
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      device.destroy()
    }
  }

  const removeDevice = async () => {
    deviceWanted = false
    cancelRetry()
    await discardDevice()
  }

  const scheduleRetry = () => {
    if (!deviceWanted || retryTimer) { return }
    if (retriesLeft <= 0) {
      snackbar.error('Could not connect the call device. Please refresh the page to make calls.')
      return
    }
    retriesLeft--
    retryTimer = setTimeout(() => {
      retryTimer = null
      setCallDevice()
    }, retryDelay)
    retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY)
  }

  const removeActiveCall = (sid: string) => {
    const updated = new Map(activeCalls.value)
    updated.delete(sid)
    activeCalls.value = updated
    // Only the ringing leg ending silences the ringtone
    if (callStore.ringingCall?.call?.parameters.CallSid === sid) { stopRingtone() }
  }

  const trackCall = (call: Call) => {
    const sid = call.parameters.CallSid
    if (sid) {
      const updated = new Map(activeCalls.value)
      updated.set(sid, call)
      activeCalls.value = updated
      callStore.addRingingCallEntry(call)
      call.on('accept', () => {
        callStore.acceptRingingCall()
        stopRingtone()
      })
      call.on('disconnect', () => removeActiveCall(sid))
      call.on('cancel', () => removeActiveCall(sid))
      call.on('reject', () => removeActiveCall(sid))
    }
  }

  const getCallBySid = (callSid: string): Call | undefined => {
    return activeCalls.value.get(callSid)
  }

  const setCallDevice = async () => {
    deviceWanted = true
    cancelRetry()
    if (callDevice.value) {
      // eslint-disable-next-line no-console
      console.log('>>> Device already exists <<<')
      return
    }
    try {
      const token = await getToken()
      // A retry runs outside the plugin's queue, so a teardown can land during the fetch.
      if (!deviceWanted) { return }
      callDevice.value = new Device(token, { allowIncomingWhileBusy: true })
      // Disabling Twilio's incoming ringtone
      callDevice.value?.audio?.incoming(false)
      callDevice.value?.on('error', (error) => {
        Sentry.captureException(error)
        discardDevice().then(scheduleRetry)
      })
      callDevice.value?.on('tokenWillExpire', async () => {
        const token = await getToken()
        callDevice.value?.updateToken(token)
      })
      callDevice.value?.on('incoming', (call: Call) => {
        trackCall(call)
      })
      callDevice.value?.on('registered', () => {
        retryDelay = FIRST_RETRY_DELAY
        retriesLeft = MAX_RETRIES
        // eslint-disable-next-line no-console
        console.log('>>> DEVICE REGISTERED SUCCESSFULLY <<<')
      })
      callDevice.value?.on('unregistered', () => {
        // eslint-disable-next-line no-console
        console.log('>>> DEVICE UNREGISTERED <<<')
      })
      await callDevice.value?.register()
    } catch (error) {
      Sentry.captureException(error)
      await discardDevice()
      scheduleRetry()
    }
  }

  return {
    callDevice,
    micPermission,
    requestMicPermission,
    setCallDevice,
    removeDevice,
    getCallBySid,
    stopRingtone,
    ...audio
  }
}
